import { CountrySelect } from "@/Common/Components/Contacts/CountrySelect";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { HealthCardValidatorFactory } from "@/Common/Components/Patient/HealthCardComponents/HealthCardValidator";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { HealthCardHelper } from "@/Common/Helpers/HealthCareHelper";
import { GeographicInfoState } from "@/Common/Models/Localization/GeographicInfoState";
import { ExpressEditMode } from "@/Common/Models/Patient/PatientEnums";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";

import styles from "./Identifiers.module.scss";

interface HealthCardsEditProps {
    healthCards: PatientIdentifier[];
    autofocusOperation?: ExpressEditMode;
    onAddHealthCard: () => void;
    onRemoveHealthCard: (item: PatientIdentifier) => void;
    onNamNumberChanged: (item: PatientIdentifier, number: string) => void;
    onNamCountryChanged: (item: PatientIdentifier, selectedCountryId: string) => void;
    onNamProvinceChanged: (item: PatientIdentifier, selectedProvinceId: string) => void;
    onNamVersionChanged: (item: PatientIdentifier, version: string) => void;
    onNamExpirationDateChange: (item: PatientIdentifier, value: Date) => void;
}

function HealthCardsEdit(props: HealthCardsEditProps): JSX.Element {
    const { getText } = useTranslation();

    const mexicoCountryLookup = cacheStore.FindLookupByLookupCode("CNMEXICO");

    const onNamNumberChanged = (healthCard: PatientIdentifier, value: string, isSelectedProvinceOntario: boolean): void => {
        const cardNumber = isSelectedProvinceOntario ? formatOntarioNumber(value) : value;

        props.onNamNumberChanged(healthCard, cardNumber);
    };

    const onNamExpirationDateChange = (item: PatientIdentifier, value: string, isMonth: boolean): void => {
        let date: Date = null;

        if (value) {
            date = item.ExpiryDate ? new Date(item.ExpiryDate) : new Date();
            date = isMonth ? new Date(date.getFullYear(), Number(value), 0) : new Date(Number(value), date.getMonth(), date.getDate());
        }

        props.onNamExpirationDateChange(item, date);
    };

    const formatOntarioNumber = (healthCardNumber: string): string =>
        healthCardNumber.endsWith("-") ? healthCardNumber.slice(0, -1) : healthCardNumber;

    return (
        <table>
            {props.healthCards.map((healthCard, idx) => {
                const isMexicoCURP = healthCard.CountryLookupId == mexicoCountryLookup.LookupID;
                const isSelectedProvinceOntario = healthCard.ProvinceLookupId == cacheStore.FindLookupByLookupCode(CanadaState.Ontario).LookupID;
                const healthcardValidator = HealthCardValidatorFactory.getValidator(healthCard.CountryLookupId, healthCard.ProvinceLookupId);

                // set available countries/regions
                if (healthCard.ProvinceLookupId) {
                    healthCard.CountryLookupId = cacheStore.GetCountryForRegion(healthCard.ProvinceLookupId).LookupID;
                    healthCard.AvailableCountryLookups = cacheStore.GetCountryLookups();
                    healthCard.AvailableCountryRegionLookups = cacheStore.GetCountryRegionLookups(cacheStore.GetCountryForRegion(healthCard.ProvinceLookupId));
                }

                const expirationYear = healthCard.ExpiryDate
                    ? LuxonDateTimeParser.parse(healthCard.ExpiryDate).year.toString()
                    : "";

                const expirationMonth = healthCard.ExpiryDate
                    ? (LuxonDateTimeParser.parse(healthCard.ExpiryDate).month).toString()
                    : "";

                return (
                    <tbody key={`hc${idx}_${healthCard.HealthIdentification_id}`} className={styles.identifiers}>
                        <tr>
                            <td className="c1">
                                <label className="form-label">{getText("CountryLabel")}</label>
                            </td>
                            <td className="c2">
                                <CountrySelect
                                    dataCy="patientProfile-healthCard-country"
                                    onCountryChange={value => props.onNamCountryChanged(healthCard, value)}
                                    initialCountryLookupId={healthCard.CountryLookupId}
                                />
                            </td>
                            <td className="c3 with-line" rowSpan={6}>
                                <span
                                    style={{ color: "red", fontWeight: "bold", cursor: "pointer" }}
                                    data-cy="patientProfile-healthCard-remove"
                                    onClick={() => props.onRemoveHealthCard(healthCard)}
                                >X
                                </span>
                            </td>
                        </tr>

                        <tr>
                            <td className="c1">
                                <label className="form-label">{getText("StateProvinceLabel")}</label>
                            </td>
                            <td className="c2">
                                <SelectLookupControl
                                    dataCy="patientProfile-healthCard-state"
                                    IncludeEmptyChoice={false}
                                    Value={healthCard.ProvinceLookupId}
                                    AvailableValues={healthCard.AvailableCountryRegionLookups.map(x => x.ToComboboxItem())}
                                    Callback={value => props.onNamProvinceChanged(healthCard, value)}
                                    Item={healthCard}
                                />
                            </td>
                        </tr>

                        <tr>
                            <td className="c1">
                                <label className="form-label">{healthcardValidator.healthCardLabel}</label>
                            </td>
                            <td className="c2">
                                <TextboxMaskedControl
                                    dataCy="patientProfile-healthCard-healthNumber"
                                    isValid={healthCard.IsValid}
                                    id={"healthNumber" + healthCard.HealthIdentification_id}
                                    value={healthCard.Number}
                                    onChangeFunction={value => onNamNumberChanged(healthCard, value, isSelectedProvinceOntario)}
                                    item={healthCard}
                                    mask={healthcardValidator.mask}
                                    validationErrorMessage={healthcardValidator.errorMessage}
                                    focus={props.autofocusOperation == ExpressEditMode.NAM}
                                    returnMaskedValue={isSelectedProvinceOntario}
                                />
                            </td>
                        </tr>

                        {!isMexicoCURP && (
                            <>
                                {!isSelectedProvinceOntario && (
                                    <tr>
                                        <td className="c1">
                                            <label className="form-label">{getText("HealthCardVersionLabel")}</label>
                                        </td>
                                        <td className="c2">
                                            <TextboxControl
                                                dataCy="patientProfile-healthCard-version"
                                                value={healthCard.Version}
                                                className="form-control"
                                                onChange={value => props.onNamVersionChanged(healthCard, value)}
                                                item={healthCard}
                                            />
                                        </td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="c1">
                                        <label className="form-label">{getText("ExpiryLabel")}</label>
                                    </td>
                                    <td className="c2">
                                        {GeographicInfoState.isNewFoundlandProvince(healthCard.ProvinceLookupId) ? (
                                            <DateSelector
                                                id={"health_card_date-picker_" + idx}
                                                width={180}
                                                cssClass="rounded"
                                                date={healthCard.ExpiryDate}
                                                onChangeCallback={date => props.onNamExpirationDateChange(healthCard, date)}
                                                dateFormat="yyyy/MM/dd"
                                            />
                                        ) : (
                                            <>
                                                <div className="inline-block" style={{ marginRight: "10px" }}>
                                                    <select
                                                        data-cy="patientProfile-healthCard-expiryYears"
                                                        onChange={event => onNamExpirationDateChange(healthCard, event.target.value, false)}
                                                        className="form-control myle-control"
                                                        value={expirationYear}
                                                    >
                                                        <option key="" value="" />
                                                        {HealthCardHelper.getExpiryYears().map(p => (
                                                            <option key={p} value={p.toString()}> {p.toString()}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="inline-block">
                                                    <select
                                                        data-cy="patientProfile-healthCard-expiryMonths"
                                                        onChange={event => onNamExpirationDateChange(healthCard, event.target.value, true)}
                                                        className="form-control myle-control"
                                                        value={expirationMonth}
                                                    >
                                                        <option key="" value="" />
                                                        {HealthCardHelper.getExpiryMonths().map(p => (
                                                            <option key={p} value={p.toString()}> {p.toString()}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </>
                                        )
                                        }
                                    </td>
                                </tr>
                            </>
                        )}

                    </tbody>
                );
            })}
            <tbody key="addHealthCard">
                <tr>
                    <td className="c1" />
                    <td className="c2" style={{ textAlign: "right" }}>
                        <a
                            data-cy="patientProfile-healthCard-add"
                            onClick={e => {
                                e.preventDefault();
                                props.onAddHealthCard();
                            }}
                        >{getText("AddLabel")}
                        </a>
                    </td>
                </tr>
            </tbody>
        </table>
    );
}

export { HealthCardsEdit };
